import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'
import { useAuth } from '../auth.jsx'
import LanguageSwitch from '../components/LanguageSwitch.jsx'
import { Wordmark } from '../components/Logo.jsx'

const inputClass =
  'h-[50px] rounded-xl border-[1.5px] border-edge px-4 text-base font-medium text-ink outline-none focus:border-teal'

const PROVIDERS = [
  { id: 'google', icon: '/brand/google-icon.svg', className: 'border-[1.5px] border-edge bg-surface text-ink hover:border-ink' },
  { id: 'apple', icon: '/brand/apple-white.svg', className: 'bg-black text-white hover:opacity-90' },
  { id: 'facebook', icon: '/brand/facebook.svg', className: 'border-[1.5px] border-edge bg-surface text-ink hover:border-ink' },
]

/** Firebase error codes → short human text (falls back to the raw message). */
const errorText = (e) =>
  (e?.code ?? '').replace('auth/', '').replaceAll('-', ' ') || e?.message || 'Error'

export default function LoginPage() {
  const { t } = useTranslation()
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [mode, setMode] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [status, setStatus] = useState(null)
  const [busy, setBusy] = useState(false)
  const register = mode === 'register'
  const done = () => navigate(location.state?.from ?? '/', { replace: true })

  const run = async (fn) => {
    setBusy(true)
    setStatus(null)
    try {
      await fn()
      done()
    } catch (e) {
      setStatus({ ok: false, text: errorText(e) })
    } finally {
      setBusy(false)
    }
  }

  const submit = (e) => {
    e.preventDefault()
    run(() => (register ? auth.register(email, password) : auth.loginWithEmail(email, password)))
  }

  const forgot = async () => {
    if (!email) return setStatus({ ok: false, text: t('login.enterEmail') })
    try {
      await auth.resetPassword(email)
      setStatus({ ok: true, text: t('login.resetSent') })
    } catch (e) {
      setStatus({ ok: false, text: errorText(e) })
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center gap-8 bg-mint px-4 pt-8 pb-14">
      <Link to="/" className="flex items-center gap-2.5 no-underline">
        <img src="/brand/urbanpurr-mark.svg" alt="" className="h-10 w-12" />
        <Wordmark className="text-[26px]" />
      </Link>

      <div className="flex w-full max-w-[440px] flex-col gap-[22px] rounded-[28px] bg-surface px-6 py-9 shadow-[0_20px_50px_rgba(30,42,53,0.08)] sm:px-8">
        <div className="flex flex-col gap-1.5 text-center">
          <h1 className="text-3xl font-black tracking-[-0.6px]">{register ? t('login.registerTitle') : t('login.title')}</h1>
          <p className="text-base text-muted">{register ? t('login.registerLead') : t('login.lead')}</p>
        </div>

        <div className="flex flex-col gap-2.5">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={!auth.firebaseEnabled || busy}
              title={auth.firebaseEnabled ? undefined : t('login.socialOff')}
              onClick={() => run(() => auth.loginWithProvider(p.id))}
              className={`flex h-[54px] cursor-pointer items-center justify-center gap-3 rounded-[14px] text-base font-extrabold transition disabled:cursor-not-allowed disabled:opacity-50 ${p.className}`}
            >
              <img src={p.icon} alt="" className="size-5" />
              {t(`login.${p.id}`)}
            </button>
          ))}
          {!auth.firebaseEnabled && <p className="text-center text-[13px] text-subtle">{t('login.socialOff')}</p>}
        </div>

        <div className="flex items-center gap-3 text-sm font-bold text-faint">
          <span className="h-px flex-1 bg-line" />
          {t('login.orEmail')}
          <span className="h-px flex-1 bg-line" />
        </div>

        <form className="flex flex-col gap-3.5" onSubmit={submit}>
          <label className="flex flex-col gap-1.5 text-sm font-extrabold">
            {t('login.email')}
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-extrabold">
            <span className="flex justify-between">
              <span>{t('login.password')}</span>
              {!register && auth.firebaseEnabled && (
                <button type="button" onClick={forgot} className="cursor-pointer font-bold text-teal hover:text-teal-dark">
                  {t('login.forgot')}
                </button>
              )}
            </span>
            <input
              type="password"
              required={auth.firebaseEnabled}
              minLength={auth.firebaseEnabled ? 6 : undefined}
              autoComplete={register ? 'new-password' : 'current-password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="h-[54px] cursor-pointer rounded-[14px] bg-teal text-[17px] font-extrabold text-on-teal transition hover:bg-teal-dark disabled:opacity-60"
          >
            {register ? t('login.submitRegister') : t('login.submit')}
          </button>
          {status && (
            <p role="status" className={`text-center text-sm font-bold ${status.ok ? 'text-teal' : 'text-live'}`}>
              {status.text}
            </p>
          )}
          {auth.devEnabled && <p className="text-center text-[13px] text-subtle">{t('login.devNote')}</p>}
        </form>

        <p className="text-center text-[15px] text-muted">
          {register ? t('login.haveAccount') : t('login.newHere')}{' '}
          <button
            type="button"
            onClick={() => setMode(register ? 'login' : 'register')}
            className="cursor-pointer font-extrabold text-teal hover:text-teal-dark"
          >
            {register ? t('login.signIn') : t('login.create')}
          </button>
        </p>
      </div>

      <p className="max-w-[440px] text-center text-[13px] leading-normal text-muted">
        {t('login.termsPrefix')} <a href="#" className="text-teal">{t('login.terms')}</a> {t('login.and')}{' '}
        <a href="#" className="text-teal">{t('login.privacy')}</a>.
      </p>
      <LanguageSwitch />
    </div>
  )
}
